import type { ManagePageId } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { meQuery, waitingQuery } from '../api';
import { LevelTwoTabs } from '../shell';
import { manageBarFor, manageGroupsOf, manageMainOf } from './pages';
import { manageWaiting } from './waiting';

/**
 * Manage's Level 2 (APP-236, option C): two rows in one sticky bar. Row 1 is the person's groups
 * as pills, main work first and then a divider (`<nav aria-label="Manage groups">`); row 2 is the
 * current group's pages (`<nav aria-label="<Group> pages">`). Both come from `me.manage`, already in
 * display order, the rule the header's Manage comes from too, so the two never disagree.
 *
 * - One group: no row 1, its pages alone.
 * - One group with one page (Payroll only): no Level 2 at all, as every Level 2 (NAV0).
 * - On a narrow screen each row scrolls sideways inside the bar (QA-052).
 *
 * `current` is the page this is: the first of them the person has is marked, so a page several
 * pages lead to (a supervisor's decision, reached from Spot decisions or Spot awards) marks the
 * one they have.
 *
 * Step 2 (§5): the counts are Home's Waiting on you, the same query the header's bell reads
 * (`waitingQuery`, so no request of its own), by the Manage page each row opens. Until it has
 * loaded, or when it can't be read, there are simply no counts. A sample preview has none: the
 * server sends a previewing tester no Waiting on you.
 */
export function ManageTabs({
  current,
  actions,
}: {
  current: ManagePageId | readonly ManagePageId[] | null;
  /** Controls on the right of the bar (Approvals' decisions by kind). */
  actions?: ReactNode;
}): ReactElement | null {
  const { data: me } = useQuery(meQuery);
  const { data: waiting } = useQuery(waitingQuery);
  if (me === undefined) return null;
  const wanted = current === null ? [] : typeof current === 'string' ? [current] : current;
  const groups = manageGroupsOf(me);
  const bar = manageBarFor(
    groups,
    manageMainOf(me)?.id ?? null,
    wanted,
    manageWaiting(waiting?.items ?? [], groups),
  );
  if (bar === null)
    return actions === undefined ? null : (
      <LevelTwoTabs label="Manage" items={[]} actions={actions} />
    );
  return (
    <LevelTwoTabs
      label={`${bar.groupLabel} pages`}
      items={bar.items}
      current={bar.current}
      groups={bar.groups}
      {...(actions !== undefined && { actions })}
    />
  );
}
